import React, { useContext, useEffect, useState } from "react";
import { 
	Button, 
	Col, 
	Drawer, 
	List, 
	Row, 
	Space, 
	Divider, 
	message, 
	Badge, 
	Input, 
	theme, 
	Table, 
	Popconfirm 
} from "antd";
import { RecurrenciesContext } from "../../../contexts";
import { useCalculateListSizeForPaginationHook, useCalculateWindowSizeForTextLength } from "../../../hooks";
import {
	SearchOutlined,
	RedoOutlined
} from "@ant-design/icons";
import PropTypes from "prop-types";
import { 
	emit_invoice,
	create_recurrency, 
	edit_recurrency, 
	get_recurrencies, 
	toggle_recurrency,
	dteNoIVA, 
	ivaRegimenExento, 
	taxesByDte,	
} from "../../../services";
import { reduceText } from "../../../utils/Formaters";
import { FillInvoiceData } from "./FillInvoiceData";
import { describeFrecuency, describeNextFrecuencyDate } from "./DescribeFrecuency";
import { FrequencyRules, calculateTaxValue } from "./components";
import { deepMerge } from "../../../utils/ObjectOperators";
import { CircleAnimationCompact } from "../../loading";
import { formatNumber } from "../../../utils/CalculateInvoiceTotal";
import { hover_item } from "../../../hover";
import { format } from "date-fns/format";
import { UserRols } from "../../../constants";

const recurrenceRuleEmpty = {
	frequency: null,
	interval: 0,
	start_date: null,
	end_date: null,
};

export const RecurrenciesDrawer = ({token, user, company, clients}) => {

	const { token: antToken } = theme.useToken();

	const { isDrawerOpen, toggleDrawer } = useContext(RecurrenciesContext);
	const { pageSize } = useCalculateListSizeForPaginationHook(90);
	const [ currentPage, setCurrentPage ] = useState(1);
	const [ createRecurrencyDrawer, setCreateRecurrencyDrawer ] = useState(false);
	const [ editRecurrencyDrawer, setEditRecurrencyDrawer ] = useState(false);
	const [ selectedEmitionRecurrence, setSelectedEmitionRecurrence ] = useState(null);
	const [ recurrenceRules, setRecurrenceRules ] = useState(recurrenceRuleEmpty);
	const [ invoice, setInvoice ] = useState({});
	const [ loading, setLoading ] = useState(false);
	const [ hoveredItem, setHoveredItem ] = useState(null);

	const [ recurrencies, setRecurrencies ] = useState([]);
	const [ recurrenciesBackup, setRecurrenciesBackup ] = useState([]);

	const { textLength: baseTextLength } = useCalculateWindowSizeForTextLength();

	const [searchText, setSearchText] = useState("");
	
	useEffect(() => {
		const currentDate = new Date();
		const formattedDate = format(currentDate, "yyyy-MM-dd");
		setRecurrenceRules({ ...recurrenceRules, start_date: formattedDate });
	}, []);

	const fetchRecurrencies = async () => {
		setLoading(true);
		const localRecurrencies = localStorage.getItem(`invoice_emition_recurrencies_${company.id}`);
		if (localRecurrencies) {
			setRecurrencies(JSON.parse(localRecurrencies)); 
			setRecurrenciesBackup(JSON.parse(localRecurrencies));
			setLoading(false);
		}

		const res = await get_recurrencies(token, "invoice_emition", company.id, "in");

		if(res){

			const sortedRes = res.sort((a, b) => {
				const nameA = a.specs?.invoice_information?.receptor?.name?.toLowerCase() || '';
				const nameB = b.specs?.invoice_information?.receptor?.name?.toLowerCase() || '';
				return nameA.localeCompare(nameB);
			});
			
			setRecurrencies(sortedRes);
			setRecurrenciesBackup(sortedRes);
			localStorage.setItem(`invoice_emition_recurrencies_${company.id}`, JSON.stringify(sortedRes));
		}

		setLoading(false);
	};
	
	useEffect(() => {
		if(!isDrawerOpen) return;
		
		// ! Recomend to comment if will be debugged and edited.
		fetchRecurrencies();
	}, [isDrawerOpen]);

	const onChangeSearch = (text) => {		
		setSearchText(text);

		const matchesText = (str) => {
			if (typeof str === "string") {
				return str.toLowerCase().includes(text.toLowerCase());
			}
			else if (typeof str === "number") {
				return String(str).toLowerCase().includes(text.toLowerCase());
			}
			return false;
		};

		const checkObjectForMatch = (obj) => {
			for (const key in obj) {
				if (matchesText(obj[key])) {
					return true;
				}

				if (typeof obj[key] === "object" && obj[key] !== null) {
					if (checkObjectForMatch(obj[key])) {
						return true;
					}
				}
			}
			return false;
		};

		if (text === "") {
			setRecurrencies(recurrenciesBackup);
		} else {
			const resultado = recurrenciesBackup.filter((recurrency) =>
				checkObjectForMatch(recurrency)
			);
			setRecurrencies(resultado);
		}
	};
	
	const editClient = async (data) => {
		setLoading(true);

		const specs = {
			recurrence_rule: {
				frequency: selectedEmitionRecurrence?.specs?.recurrence_rule?.frequency,
				interval: selectedEmitionRecurrence?.specs?.recurrence_rule?.interval,
				start_date: selectedEmitionRecurrence?.specs?.recurrence_rule?.start_date,
				end_date: selectedEmitionRecurrence?.specs?.recurrence_rule?.end_date || "",
			},
			invoice_information: {
				specs: {
					xchange_rate: selectedEmitionRecurrence?.specs?.invoice_information?.specs?.xchange_rate,
				},
				frases: selectedEmitionRecurrence?.specs?.invoice_information?.frases,
				type: selectedEmitionRecurrence?.specs?.invoice_information?.type,
				currency: selectedEmitionRecurrence?.specs?.invoice_information?.currency,
				establishment_id: selectedEmitionRecurrence?.specs?.invoice_information?.establishment_id,
				receptor: selectedEmitionRecurrence?.specs?.invoice_information?.receptor,
				items: selectedEmitionRecurrence?.specs?.invoice_information?.items.map(item => ({
					quantity: Number(item.quantity),
					description: item.description,
					priceUnit: Number(item.priceUnit),
					discount: item.discount ? Number(item.discount)	: 0,
					taxes: item.taxes.map(tax => ({
						shortName: `${tax.shortName}`,
					})),
					type: item.type,
					accounting_account: item.accounting_account || "",
				})),
				email: data?.email,
			},
		};
		
		try {
			const res = await edit_recurrency(token, selectedEmitionRecurrence.id, "invoice_emition", specs);

			if(res){
				message.success(res);				
				setEditRecurrencyDrawer(false);				

				const index = recurrencies.findIndex(r => r.id === selectedEmitionRecurrence.id);
				if (index === -1) return;

				const updatedRecurrencies = [...recurrenciesBackup];
				const recurrence = updatedRecurrencies[index];
				const updatedSpecs = deepMerge(recurrence.specs, specs);
				const updatedRecurrence = { ...recurrence, specs: updatedSpecs };
				updatedRecurrencies[index] = updatedRecurrence;
				
				setRecurrenciesBackup(updatedRecurrencies);

				onChangeSearch(searchText);

				setSelectedEmitionRecurrence(null);
			}
		} catch (error) {
			message.error(error.message);
		} finally {
			setLoading(false);
		}			
	};
	
	const processEmitionRecurrence = async (data) => {
		const specs = {
			recurrence_rule: {
				frequency: recurrenceRules.frequency,
				interval: recurrenceRules.interval,
				start_date: recurrenceRules.start_date,
				end_date: recurrenceRules.end_date || "",
			},
			invoice_information: {
				sender: {
					address: company.address,
					id: company.id,
					tid: company.tid,
					name: company.name,
				},
				receptor: {
					address: invoice.client.address,
					id: invoice.client.id,
					name: invoice.client.name,
					tid: invoice.client.tid,
					type: invoice.client.type || "",
				},
				specs: {
					xchange_rate: invoice.xchange_rate !== undefined ? Number(invoice.xchange_rate) : null,
					instructions: company.invoice_emition_specifications.instructions || "",
				},
				frases: invoice.frases,
				type: invoice.type,
				currency: invoice.currency,
				establishment_id: invoice.establishment,
				items: invoice.items.map(item => ({
					quantity: Number(item.quantity),
					description: item.description,
					priceUnit: Number(item.priceUnit),
					discount: item.discount ? Number(item.discount)	: 0,
					taxes: item.taxes.map(tax => ({
						shortName: tax,
					})),
					type: item.type,
					accounting_account: (item.accounting_account || "").toString(),
				})),
				email: data?.email,
			},
		};


		try {
			setLoading(true);
					
			const res = await create_recurrency(token, "invoice_emition", company.id, specs, "in");
			if(res){
				message.success("Recurrencia creada con exito");
				setCreateRecurrencyDrawer(false);
				setRecurrenceRules(recurrenceRuleEmpty);
				const newRecurrence = {
					id: res.id,
					type: "invoice_emition",
					company_id: company.id,
					receptor_id: "",
					sender_id: company.tid,
					position: "in",
					active: true,
					specs: specs
				};
				setRecurrencies([...recurrencies, newRecurrence]);
				setInvoice({});
			}			
		} catch (error) {
			message.error(error.message);
		} finally {
			setLoading(false);
		}	
	};

	const processNotification = async () => {

		const data = selectedEmitionRecurrence.specs.invoice_information;

		const notification = {
			token,
			emition_value: "individual-recurrency",
			country: "GT",
			data: {
				recurrency_id: selectedEmitionRecurrence.id,
				sender: {
					address: company.address,
					id: company.id,
					tid: company.tid,
					name: company.name,
				},
				receptor: {
					address: data.receptor.address,
					id: data.receptor.id,
					name: data.receptor.name,
					tid: data.receptor.tid,
					type_tid: data.receptor.type || "",
				},
				specs: {
					xchange_rate: data.specs.xchange_rate !== undefined ? String(data.specs.xchange_rate) : "",
					instructions: company.invoice_emition_specifications.instructions || "",
				},
				frases: data.frases,
				type: data.type,
				currency: data.currency,
				establishment_id: data.establishment_id,
				items: data.items.map(item => ({
					quantity: Number(item.quantity),
					description: item.description,
					priceUnit: Number(item.priceUnit),
					discount: item.discount ? Number(item.discount)	: 0,
					taxes: item.taxes,
					type: item.type,
					accounting_account: (item.accounting_account || "").toString(),
				})),
				email: data?.email,
			},
		};
	
		setLoading(true);

		try {
			const res = await emit_invoice(notification);
			if (res) {
				message.success(res.data);
			}
		} catch (error) {
			message.error(error.response.data);
		} finally {
			setInvoice({});
			setLoading(false);
		}
	};

	const calculateTotalDescription = (quantity, price, discount = 0, taxes = [], dteType, currency, company) => {
		// Asegurar que quantity, price, discount son números válidos, de lo contrario, asignar 0
		quantity = isNaN(quantity) ? 0 : quantity;
		price = isNaN(price) ? 0 : price;
		discount = isNaN(discount) ? 0 : discount;
    
		const base = quantity * price;
		const baseWithDiscount = base - discount;
        
		// Asegurar que baseWithDiscount es válido y no NaN
		const baseWithDiscountSafe = isNaN(baseWithDiscount) ? 0 : baseWithDiscount;
    
		let baseNoIVA = baseWithDiscountSafe / 1.12;
		let iva = baseNoIVA * 0.12;

		// Validar baseNoIVA e iva
		baseNoIVA = isNaN(baseNoIVA) ? 0 : baseNoIVA;
		iva = isNaN(iva) ? 0 : iva;
    
		let additionalCosts = [];
    
		// Comprobar si el régimen de IVA es exento
		if (ivaRegimenExento.includes(company.tax_authority_specifications.iva_regimen) || dteNoIVA.includes(dteType)) {
			baseNoIVA = baseWithDiscountSafe;
			iva = 0;
		}

    
		taxes.forEach(tax => {
			const taxDetails = taxesByDte[dteType][tax];
			const value = calculateTaxValue(taxDetails, quantity, price, discount, currency, 7.8, company);
            
			// Validar el valor calculado del impuesto
			const safeValue = isNaN(value) ? 0 : value;
    
			additionalCosts.push([tax, safeValue]);
		});
    
		let total = baseWithDiscountSafe;
		additionalCosts.forEach(tax => {
			if (tax[0] !== "IVA" && tax[0] !== "IVA EXENTO") {
				total += tax[1];
			}
		});
    
		// Asegurarse de que el total sea válido y no NaN
		total = isNaN(total) ? 0 : total;
    
		let detailed = {
			base: isNaN(base) ? 0 : base,
			discount: isNaN(discount) ? 0 : discount,
			baseWithDiscount: baseWithDiscountSafe,
			iva,
			baseNoIVA,
			additionalCosts,
			total
		};
    
		return { total, detailed };
	};

	// Función separada para actualizar y formatear los ítems con el total calculado
	const updateItemsWithTotal = (items, dteType, currency) => {
		return items.map((item) => {
			const {total, detailed} = calculateTotalDescription(
				item.quantity, 
				item.priceUnit, 
				item.discount, 
				item.taxes,
				dteType,
				currency,
				company
			);

			return {
				...item,
				total,
				detailed
			};
		});
	};

	const getRecurrencyTotal = (item) => {
		const item_totals = updateItemsWithTotal(
			item?.specs?.invoice_information?.items.map(item => ({
				quantity:item.quantity,
				priceUnit:item.priceUnit,
				discount:item.discount,
				taxes:item.taxes,
			})), 
			item?.specs?.invoice_information?.type, 
			item?.specs?.invoice_information?.currency);
		
		const total = item_totals.reduce((acc, item) => {
			return acc + parseFloat(item.total);
		}, 0);

		return formatNumber(total);
	};

	const toggleRecurrency = async () => {
		try {
			setLoading(true);
	
			const res = await toggle_recurrency(token, selectedEmitionRecurrence.id);
			if (res) {
				// Actualizar el estado de recurrencies
				setRecurrencies(prevRecurrencies => 
					prevRecurrencies.map(recurrence => 
						recurrence.id === selectedEmitionRecurrence.id 
							? { ...recurrence, active: !recurrence.active } 
							: recurrence
					)
				);
	
				// Limpiar las reglas de recurrencia y actualizar la selección actual
				setRecurrenceRules(recurrenceRuleEmpty);
				setSelectedEmitionRecurrence({
					...selectedEmitionRecurrence, 
					active: !selectedEmitionRecurrence.active
				});
			}
		} catch (error) {
			// ...
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Drawer
				title="Recurrencias"
				placement="right"
				open={isDrawerOpen}
				onClose={toggleDrawer}
				width={"100vw"}
				extra={
					<Space>
						<Button
							type="primary"
							onClick={() => setCreateRecurrencyDrawer(true)}
							loading={loading}
						>
							Crear Nueva Recurrencia
						</Button>
						<Button
							type="primary"
							onClick={fetchRecurrencies}
							loading={loading}
							icon={<RedoOutlined />}
						/>
					</Space>
				}
			>
				{loading ? 
					<CircleAnimationCompact /> :
					<>
						<Input
							placeholder="Palabra Clave"
							value={searchText}
							onChange={(e) => onChangeSearch(e.target.value)}
							prefix={
								<SearchOutlined />
							}
						/>
						<List
							header={
								<Row
									style={{
										background: antToken.colorBgLayout,
										borderRadius:"10px"
									}}
								>
									<Col flex="auto">
										<List.Item key={0} >
											<List.Item.Meta
												style={{
													textAlign: "center",
													minWidth:50,
													maxWidth:120
												}}	
												title="Estado"
												description="Próxima Emisión"
											/>
											<List.Item.Meta
												style={{
													textAlign: "left",
													minWidth: 300
												}}	
												title="Cliente"
												description="Dirección"
											/>
											<List.Item.Meta 
												style={{
													textAlign: "left",
													minWidth:100,
													maxWidth:300
												}} 
												title="Establecimiento"
												description="Dirección"
											/>
											<List.Item.Meta 
												style={{
													textAlign:"right",
													minWidth:50,
													maxWidth:150
												}} 
												title="Total" 
												description="Moneda" 
											/>
											<List.Item.Meta 
												style={{
													textAlign:"center",
													minWidth:50,
													maxWidth:120
												}} 
												title="Frecuencia"
												description="Fecha Final"
											/>
											<List.Item.Meta
												style={{
													textAlign:"center",
													minWidth:50,
													maxWidth:120
												}} 
												title="Última Emisión"
											/>
										</List.Item>
									</Col>
								</Row>
							}
							pagination={{
								current: currentPage,
								onChange: (page) => setCurrentPage(page),
								position: "top",
								align: "center",
								disabled: false,
								showSizeChanger: false,
								pageSize: pageSize,
								showTotal:(total, range) => `${range[0]}-${range[1]} de ${total} Recurrencias`
							}}
							style={{
								textAlign: "start",
							}}
							size="small"
							dataSource={recurrencies || []}
							renderItem={(item, index) => (
								<Row>
									<Col flex="auto">
										<List.Item
											key={index}
											onMouseEnter={() => setHoveredItem(index)}
											onMouseLeave={() => setHoveredItem(null)}
											onClick={() => {												
												setEditRecurrencyDrawer(true);
												setSelectedEmitionRecurrence(item);
											}}
											style={hover_item(hoveredItem === index, antToken, "invoices", 70)}
										>
											<List.Item.Meta
												style={{
													textAlign: "center",
													minWidth:50,
													maxWidth:120
												}}				
												title={ <Badge status={item.active? "success" : "default"} text={item.active?  "Activa" : "Inactiva"} />}
												description={item.active? describeNextFrecuencyDate(item?.specs?.recurrence_rule) : "---"}
											/>
											<List.Item.Meta	
												style={{
													textAlign: "left",
													minWidth:300,
												}}							
												title={reduceText(item?.specs?.invoice_information?.receptor?.name, baseTextLength*1.5)}
												description={reduceText(item?.specs?.invoice_information?.receptor?.address, baseTextLength*1.5)}
											/>
											<List.Item.Meta
												style={{
													textAlign:"left",
													minWidth:100,
													maxWidth:300
												}}
												title={ reduceText(company.tax_authority_specifications?.establishments?.find((establishment) => establishment.id == item?.specs?.invoice_information?.establishment_id)?.name, Math.min(baseTextLength*1.5,35)) }
												description={ reduceText(company.address, Math.min(baseTextLength*1.5,35)) }
											/>
											<List.Item.Meta
												style={{
													textAlign:"right",
													minWidth:50,
													maxWidth:150
												}}
												title={getRecurrencyTotal(item)}
												description={item?.specs?.invoice_information?.currency}
											/>
											<List.Item.Meta
												style={{
													textAlign:"center",
													minWidth:50,
													maxWidth:120
												}}
												title={describeFrecuency(item?.specs?.recurrence_rule?.frequency, item?.specs?.recurrence_rule?.interval)}
												description={item?.specs?.recurrence_rule?.end_date === "" ? "N/A" : item?.specs?.recurrence_rule?.end_date}
											/>
											<List.Item.Meta
												style={{
													textAlign: "center",
													minWidth:50,
													maxWidth:120
												}}
												title={
													<Badge
														count={
															item?.history && item.history?.length > 0
																? item.history[item.history?.length - 1]?.success
																	? "Emitida con Éxito"
																	: "Hubo un Error"
																: "Sin Historial"
														}
														showZero
														color={
															item?.history && item.history?.length > 0
																? item.history[item.history?.length - 1]?.success
																	? "green"
																	: "red"
																: "gray"
														}
													/>
												}
											/>
										</List.Item>
									</Col>
								</Row>
							)}
						/>
					</>
				}
			</Drawer>
			<Drawer
				title="Agregar Nueva Recurrencia"
				placement="right"				
				width={"100vw"}
				onClose={() => setCreateRecurrencyDrawer(false)}
				open={createRecurrencyDrawer}
			>
				{loading ? (
					<CircleAnimationCompact />
				) : (
					<>
						<Row>
							<Col span={6}/>
							<Col span={12}>
								<FrequencyRules
									recurrenceRules={recurrenceRules}
									onRecurrenceRules={(value) => setRecurrenceRules({...recurrenceRules, ...value})}
								/>
							</Col>
							<Col span={6}/>
						</Row>
						<Divider orientation="center">Información Factura Nueva</Divider>
						<FillInvoiceData
							token={token}
							company={company}
							setInvoice={setInvoice}
							invoice={invoice}
							clients={clients}
							recurrenceRules={recurrenceRules}
							onSaveChanges={(value) => {
								if(!value) return;

								processEmitionRecurrence(value);
							}}
							isCreate={true}
						/>
					</>
				)}
			</Drawer>
			<Drawer
				title="Editar Recurrencia de Emisión"
				placement="right"				
				width={"100vw"}
				onClose={() => {
					setEditRecurrencyDrawer(false);
					setSelectedEmitionRecurrence(null);
				}}
				open={editRecurrencyDrawer}
				extra={
					<Space> 
						{
							user?.role === UserRols.ARO_ADMIN && 
							<div>
								ID: {selectedEmitionRecurrence?.id}
							</div>
						}
						<Popconfirm
							title="Guardó sus cambios?"
							description="Si has hecho cambios en tu recurrencia, guarda los cambios antes de emitir la factura."
							onConfirm={processNotification}
							loading={loading}
							okText="SI"
							cancelText="CANCELAR"
						>
							<Button
								color="primary"
								variant="outlined"
								loading={loading}
							>
								Emitir Ahora
							</Button>
						</Popconfirm>
						<Button
							type="primary"
							danger = {selectedEmitionRecurrence?.active? true : false}
							onClick={toggleRecurrency}
							loading={loading}
						>
							{selectedEmitionRecurrence?.active? "Desactivar Recurrencia" : "Activar Recurrencia"}
						</Button>
					</Space>
				}
			>
				{selectedEmitionRecurrence && (
					loading ? (
						<CircleAnimationCompact />
					) : (
						<div>
							<Row gutter={[50,0]}>
								<Col span={12}>
									<FrequencyRules
										recurrenceRules={selectedEmitionRecurrence?.specs?.recurrence_rule}
										onRecurrenceRules={(value) => setSelectedEmitionRecurrence({
											...selectedEmitionRecurrence,
											specs: {...selectedEmitionRecurrence?.specs, recurrence_rule: {...selectedEmitionRecurrence?.specs?.recurrence_rule,  ...value}}
										})}
									/>
								</Col>
								<Col span={12}>
									<Divider orientation="center">Historial</Divider>
									{
										selectedEmitionRecurrence.history ? (
											<Table
												size="small"
												dataSource={selectedEmitionRecurrence.history
													.map(event => ({
														date: new Date(event.timestamp._seconds * 1000 + event.timestamp._nanoseconds / 1000000),
														status: event.success,
														message: event.message,
													}))
													.sort((a, b) => b.date - a.date)} // Ordenar del más nuevo al más antiguo
												columns={[
													{
														title: "Fecha",
														dataIndex: "date",
														key: "date",
														render: (_, record) => (
															<span>{record.date.toLocaleString()}</span> // Mostrar la fecha formateada
														),
													},
													{
														title: "Estado",
														dataIndex: "status",
														key: "status",
														render: (status) => (
															<Badge
																color={status ? "green" : "red"}
																count={status ? "Success" : "Fail"}
															/>
														), // Mostrar el Badge de estado
													},
													{
														title: "Mensaje (ver más)",
														dataIndex: "message",
														key: "message",
														render: (message) => (
															<div style={{ maxHeight: "50px", overflow: "hidden", textOverflow: "ellipsis" }}>
																{message?.length > 100 ? `${message.substring(0, 100)}...` : message}
															</div>
														),
													},
												]}
												expandable={{
													expandedRowRender: (record) => <p>{record.message}</p>, // Mostrar mensaje completo al expandir
													rowExpandable: (record) => record.message?.length > 100, // Solo filas con mensajes largos se pueden expandir
												}}
												pagination={{ pageSize: 5 }} // Paginación para mostrar 10 registros por página
											/>
										) : (
											<></>)
									}
								</Col>

							</Row>
							<Divider orientation="center">Editar Datos de la Factura</Divider>
							<FillInvoiceData
								token={token}
								company={company}
								invoice={selectedEmitionRecurrence?.specs?.invoice_information}
								setInvoice={(values) => {
									if( (values.receptor && !values.receptor.name) || (values.client && !values.client.name)) {
										throw Error("Información de Cliente Incompleta");
									} 
									const newInvoiceInformation = {
										specs: {
											xchange_rate: values?.xchange_rate || null,
										},
										receptor: {
											address: values.receptor? values.receptor.address : values.client.address,
											id: values.receptor? values.receptor.id : values.client.id,
											name: values.receptor? values.receptor.name : values.client.name,
											tid: values.receptor? values.receptor.tid : values.client.tid,
											type: values.receptor? values.receptor.type : values.client.type || "",
										},
										items: values.items.map(item => ({
											quantity: Number(item.quantity),
											description: item.description,
											priceUnit: Number(item.priceUnit),
											discount: item.discount ? Number(item.discount)	: 0,
											taxes: item.taxes.map(tax => ({
												shortName: `${tax}`,
											})),
											type: item.type,
											accounting_account: item.accounting_account || "",
										})),
										frases: values.frases,
										email: selectedEmitionRecurrence?.specs?.invoice_information?.email,
									};

									if (values.currency) newInvoiceInformation.currency = values.currency;
									if (values.establishment) newInvoiceInformation.establishment_id = values.establishment;
									if (values.type) newInvoiceInformation.type = values.type;
									
									setSelectedEmitionRecurrence({
										...selectedEmitionRecurrence,
										specs: {
											...selectedEmitionRecurrence?.specs, 
											invoice_information: {
												...selectedEmitionRecurrence?.specs?.invoice_information, 
												...newInvoiceInformation
											}
										}
									});
								}}
								clients={clients}
								recurrenceRules={selectedEmitionRecurrence?.specs?.recurrence_rule}
								emailData={selectedEmitionRecurrence?.specs?.invoice_information?.email}
								loading={loading}
								onSaveChanges={(value) => {
									if(!value) return;

									editClient(value);									
								}}
							/>
						</div>
					)
				)}
			</Drawer>
		</>		
	);
};

RecurrenciesDrawer.propTypes = {
	token: PropTypes.string.isRequired,
	user: PropTypes.object.isRequired,
	company: PropTypes.object.isRequired,
	clients: PropTypes.array
};